import React, {
  useState,
  useEffect,
  useRef,
  use,
  useImperativeHandle,
} from 'react'
import { connect } from 'react-redux'
import IFModal from 'Components/IFModal/IFModal'
import { Colors } from 'Theme'
import { useTranslation } from 'react-i18next'
import {
  IFSkeleton,
  IFText,
  ChargingSessionDetails,
  EnergyConsumptionChart,
  ConnectorStatusChart,
  IFFilter,
  IFsvg,
  IFToggle,
} from 'Components'
import Styles from './ChargingEventsModal.module.css'
import ChargePointSelectors from 'Stores/ChargePoint/Selectors'
import ChargePointActions from 'Stores/ChargePoint/Actions'
import RequestState from 'Enums/RequestState'
import { ChargingEventsModalTabs, ChargingSessionLogsList } from 'Containers'
import { increaseBrightness } from '../../Utils/Calculations'
import Scrollbars from 'react-custom-scrollbars-2'
import { Emitter } from '../../Services/EmitterService'
import AuthSelectors from 'Stores/Auth/Selectors'
import InfinityEnums from 'Enums/InfinityEnums'
import moment from 'moment'

const ChargingEventsModal = React.forwardRef(
  (
    {
      session,
      chargingSessionId,
      fetchChargingSessionDetailsRequestState,
      chargingSessionDetails,
      chargingSessionEvents,
      chargingSessionLogs,
      fetchChargingSessionEventsRequestState,
      fetchChargingSessionLogsRequestState,
      fetchChargingSessionEvents,
      fetchChargingSessionLogs,
      resetChargingSessionEvents,
      resetChargingSessionLogs,
      chargingLogsPaginationOffset,
      chargingSessionLogsFilter,
      setChargingSessionLogsFilter,
      resetChargingSessionLogsFilters,
      setChargingSessionLogsPaginationOffset,
      chargePointConnectors,
      open,
      tariffsList,
      operator,
      emitterUpdateChargingEvents,
      fetchChargingSessionDetails,
      emitterUpdateChargingSessionDetails,
      setIsChargingAnalyticsLive,
      isChargingAnalyticsLive,
      resetChargingSessionDetails,
      transaction,
    },
    ref,
  ) => {
    const customDialogContentStyle = {
      padding: 0,
      height: '60rem',
      width: '70rem',
    }
    const { t } = useTranslation()
    const [selectedDetails, setSelectedDetails] = useState(0)
    const chargingEventsModalTabsRef = useRef()

    const modalContentRef = useRef(null)
    const modalRef = useRef(null)
    const sessionDetailsRef = useRef(null)
    const [isFiltersSelected, setIsFiltersSelected] = useState(false)
    const [isLive, setIsLive] = useState(false)
    const [refreshAnalytics, setRefreshingAnalytics] = useState(false)
    const [analyticsLastUpdated, setAnalyticsLastUpdated] = useState(null)
    const [isModalLiveActive, setIsModalLiveActive] = useState(false)
    const [isSessionEnded, setIsSessionEnded] = useState(false)
    const sessionIdRef = useRef(chargingSessionDetails._id)

    useImperativeHandle(ref, () => ({
      setLive: (val) => setIsLive(val),
      getLive: () => isLive,
      setModalLiveActive: (val) => setIsModalLiveActive(val),
      show: () => modalRef.current?.show(),
      dismiss: () => modalRef.current?.dismiss(),
    }))
    useEffect(() => {
      if (!chargingSessionDetails || !chargingSessionDetails.status) {
        setIsSessionEnded(false)
        return
      }

      if (
        chargingSessionDetails.status ===
        InfinityEnums.ChargingSessionStatus.ENDED
      ) {
        setIsLive(false)
        setIsChargingAnalyticsLive(false)
        setIsSessionEnded(true)
      } else if (
        sessionIdRef.current?.toString() !==
        chargingSessionDetails?._id.toString()
      ) {
        setIsSessionEnded(false)
        setIsModalLiveActive(true)
        setIsLive(true)
        setIsChargingAnalyticsLive(true)
        sessionIdRef.current = chargingSessionDetails._id
      }
    }, [chargingSessionDetails])
    const handleModalClose = () => {
      setSelectedDetails(0)
      resetChargingSessionLogs()
      resetChargingSessionLogsFilters()
      resetChargingSessionEvents()
      resetChargingSessionDetails()
      setIsChargingAnalyticsLive(true)
      setIsSessionEnded(false)
      setIsLive(false)
    }
    const handleSelectedTab = (index) => {
      setSelectedDetails(index)
    }

    useEffect(() => {
      if (selectedDetails === 0 && chargingSessionDetails?._id && !isLive) {
        resetChargingSessionLogs()
        resetChargingSessionLogsFilters()
        fetchChargingSessionEvents(chargingSessionId)
      } else if (selectedDetails === 1 && chargingSessionId) {
        if (!isLive) resetChargingSessionEvents()
        fetchChargingSessionLogs(
          chargingSessionId,
          chargingLogsPaginationOffset,
        )
      }
    }, [selectedDetails, chargingSessionDetails?._id, chargingSessionId])

    const handleChargingSessionLogs = (newFilter) => {
      setChargingSessionLogsPaginationOffset(0)
      const adjustedFilter = newFilter?.map((filter) => {
        if (filter.value) filter.initialValue = filter.value
        return filter
      })
      setChargingSessionLogsFilter(adjustedFilter)
    }

    useEffect(() => {
      if (chargingSessionId) {
        fetchChargingSessionLogs(
          chargingSessionId,
          0,
          chargingSessionLogsFilter,
        )
      }
    }, [chargingSessionLogsFilter])
    useEffect(() => {
      if (chargingSessionId) {
        fetchChargingSessionDetails(chargingSessionId)
        fetchChargingSessionEvents(chargingSessionId)
      }
    }, [])

    useEffect(() => {
      if (chargingSessionDetails?._id) {
        resetChargingSessionEvents()
        fetchChargingSessionEvents(chargingSessionDetails?._id)
      }
    }, [chargingSessionDetails._id])

    useEffect(() => {
      const operatorId = operator?._id
      const channelKey = operator?.channelKey

      if (!operatorId || !channelKey || !chargingSessionId) return

      if (isChargingAnalyticsLive) {
        Emitter.subscribeToChannel(
          `operator/${operatorId}/charge/${chargingSessionId}/chargingEvents/`,
          channelKey,
          (log) => {
            if (log?.data) emitterUpdateChargingEvents(log.data)
          },
          {},
        )

        Emitter.subscribeToChannel(
          `operator/${operatorId}/charge/${chargingSessionId}/chargingSessionDetails/`,
          channelKey,
          (log) => {
            if (log?.data) emitterUpdateChargingSessionDetails(log.data)
          },
          {},
        )
        setIsLive(true)
      } else {
        Emitter.unsubscribeFromChannel(
          `operator/${operatorId}/charge/${chargingSessionId}/chargingEvents/`,
          channelKey,
        )
        Emitter.unsubscribeFromChannel(
          `operator/${operatorId}/charge/${chargingSessionId}/chargingSessionDetails/`,
          channelKey,
        )
        setIsLive(false)
      }
    }, [
      operator?._id,
      operator?.channelKey,
      chargingSessionId,
      isChargingAnalyticsLive,
    ])

    useEffect(() => {
      if (
        (fetchChargingSessionDetailsRequestState !== RequestState.LOADING &&
          fetchChargingSessionDetailsRequestState !==
            RequestState.UNINITIALIZED) ||
        (fetchChargingSessionEventsRequestState !== RequestState.LOADING &&
          fetchChargingSessionEventsRequestState !== RequestState.UNINITIALIZED)
      ) {
        setRefreshingAnalytics(false)
        setAnalyticsLastUpdated(Date.now())
      }
    }, [
      fetchChargingSessionEventsRequestState,
      fetchChargingSessionDetailsRequestState,
    ])
    return (
      <IFModal
        ref={modalRef}
        title={t('ChargingEventsModal.Title')}
        maxWidth="70rem"
        open={open}
        onDismiss={handleModalClose}
        dialogContentStyle={customDialogContentStyle}
      >
        <div className={Styles.Container} ref={modalContentRef}>
          <div className={Styles.ChargePointNameContainer}>
            <div className={Styles.ParentNameContainer}>
              {fetchChargingSessionDetailsRequestState ===
              RequestState.LOADING ? (
                <IFSkeleton
                  className={Styles.ChargePointNameSkeleton}
                  variant="text"
                  width="14rem"
                  height=" 3rem"
                />
              ) : (
                <div className={Styles.ChargePointNameWrapper}>
                  <div
                    className={Styles.ChargePointStationNameContainer}
                    style={{
                      backgroundColor: increaseBrightness(Colors.primary, 10),
                    }}
                  >
                    <IFText
                      style={{ color: Colors.white }}
                      className={Styles.NameText}
                    >
                      {isLive
                        ? session?.chargePoint.name
                        : fetchChargingSessionDetailsRequestState ===
                          RequestState.SUCCEEDED
                        ? chargingSessionDetails?.chargePoint.name
                        : '—'}
                    </IFText>
                  </div>
                  <div
                    className={Styles.ChargePointName}
                    style={{
                      border: `1.5px solid ${increaseBrightness(
                        Colors.primary,
                        10,
                      )}`,
                    }}
                  >
                    <IFText
                      style={{ color: Colors.text }}
                      className={Styles.NameText}
                    >
                      {isLive
                        ? session?.chargePoint.huid
                        : fetchChargingSessionDetailsRequestState ===
                          RequestState.SUCCEEDED
                        ? chargingSessionDetails?.chargePoint.huid
                        : '—'}
                    </IFText>
                  </div>
                </div>
              )}

              {isModalLiveActive && (
                <div className={Styles.RefreshIconTextContainer}>
                  {fetchChargingSessionDetailsRequestState ===
                    RequestState.LOADING ||
                  fetchChargingSessionEventsRequestState ===
                    RequestState.LOADING ||
                  !analyticsLastUpdated ? (
                    <div className={Styles.LiveLogsToggleSkeleton}>
                      <IFSkeleton
                        width="4rem"
                        height="2rem"
                        variant="text"
                        style={{ alignSelf: 'center' }}
                      />
                    </div>
                  ) : (
                    <div className={Styles.LiveLogsToggle}>
                      <IFToggle
                        height={22}
                        fontSize={12}
                        checkedText={t('ChargePointLogsToggle.Live')}
                        uncheckedText={t('ChargePointLogsToggle.Live')}
                        CheckedIcon={IFsvg.liveEncoded({
                          fill: Colors.primary,
                          width: '14',
                          height: '14',
                        })}
                        UncheckedIcon={IFsvg.notLiveEncoded({
                          fill: Colors.primary,
                          width: '14',
                          height: '14',
                        })}
                        defaultChecked={isChargingAnalyticsLive}
                        checked={isChargingAnalyticsLive}
                        onChange={(value) => {
                          setIsChargingAnalyticsLive(value)
                        }}
                        readOnly={
                          chargingSessionDetails.status ===
                          InfinityEnums.ChargingSessionStatus.ENDED
                        }
                      />
                    </div>
                  )}
                  <div
                    className={Styles.RefreshTextContainer}
                    style={{
                      opacity: isChargingAnalyticsLive ? 0 : 1,
                      pointerEvents: isChargingAnalyticsLive ? 'none' : 'auto',
                    }}
                  >
                    {fetchChargingSessionDetailsRequestState ===
                      RequestState.LOADING ||
                    fetchChargingSessionEventsRequestState ===
                      RequestState.LOADING ? (
                      <div>
                        <div className={Styles.RefreshIconTextSkeleton}>
                          <div className={Styles.RefreshIconSkeleton}>
                            <IFSkeleton
                              width="1rem"
                              height="1rem"
                              variant="circular"
                            />
                          </div>
                          <IFSkeleton
                            height="1.5rem"
                            className={Styles.RefreshTextSkeleton}
                          />
                        </div>

                        <IFSkeleton
                          height="1.25rem"
                          className={Styles.RefreshTextDateSkeleton}
                        />
                      </div>
                    ) : (
                      <>
                        <div className={Styles.LiveLogs}>
                          <div
                            className={Styles.RefreshIcon}
                            onClick={() => {
                              if (refreshAnalytics) return
                              setRefreshingAnalytics(true)
                              fetchChargingSessionEvents(
                                chargingSessionDetails?._id,
                              )
                              fetchChargingSessionDetails(
                                chargingSessionDetails?._id,
                              )
                            }}
                          >
                            <div className={Styles.RefreshSvg}>
                              <IFsvg.refresh
                                height={16}
                                width={16}
                                fill={Colors.black}
                              />
                            </div>
                            <IFText
                              className={Styles.RefreshText}
                              style={{ color: Colors.black }}
                            >
                              {t('ChargePointDetails.lastUpdated')}
                            </IFText>
                          </div>
                        </div>
                        <IFText className={Styles.RefreshTextDate}>
                          {analyticsLastUpdated
                            ? moment(analyticsLastUpdated).format(
                                'MMM DD, YYYY HH:mm:ss',
                              )
                            : ''}
                        </IFText>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <Scrollbars className={Styles.ScrollParent}>
            <div className={Styles.ChartContainer}>
              <div
                className={Styles.SessionDetailsChart}
                ref={sessionDetailsRef}
              >
                <ChargingSessionDetails
                  session={chargingSessionDetails}
                  fetchChargingSessionRequestState={
                    fetchChargingSessionDetailsRequestState
                  }
                  chargePointConnectors={chargePointConnectors}
                  tariffsList={transaction?.tariffs}
                  isLive={isLive}
                  isSessionEnded={isSessionEnded}
                  operator={operator}
                />
              </div>
            </div>
            <div className={Styles.TabsContainer}>
              <ChargingEventsModalTabs
                initialIndex={selectedDetails}
                ref={chargingEventsModalTabsRef}
                onValueChange={(index) => handleSelectedTab(index)}
                isLoading={
                  fetchChargingSessionDetailsRequestState ===
                  RequestState.LOADING
                }
              />
              <div
                className={Styles.TabsContent}
                style={
                  selectedDetails === 0
                    ? { marginTop: '1rem', height: '100%' }
                    : { height: '100%' }
                }
              >
                {selectedDetails === 0 ? (
                  <>
                    <EnergyConsumptionChart
                      startTimestamp={chargingSessionDetails.startedAt}
                      endTimestamp={chargingSessionDetails.updatedAt}
                      fetchEnergyConsumptionDataRequestState={
                        fetchChargingSessionEventsRequestState
                      }
                      sessionStartTime={chargingSessionDetails.startedAt}
                      sessionEndTime={chargingSessionDetails.updatedAt}
                      noData={chargingSessionEvents.length === 0}
                      chargingEvents={chargingSessionEvents}
                      isLive={isLive}
                    />
                    <ConnectorStatusChart
                      preparingSeriesData={chargingSessionEvents}
                      startTimestamp={chargingSessionDetails.startedAt}
                      endTimestamp={chargingSessionDetails.updatedAt}
                      fetchConnectorStatusRequestState={
                        fetchChargingSessionEventsRequestState
                      }
                      noData={chargingSessionEvents.length === 0}
                      isLive={isLive}
                    />
                  </>
                ) : (
                  <div className={Styles.LogsDiv}>
                    <div className={Styles.FilterContainer}>
                      {(fetchChargingSessionLogsRequestState ===
                        RequestState.LOADING ||
                        (fetchChargingSessionLogsRequestState ===
                          RequestState.SUCCEEDED &&
                          (chargingSessionLogs.length > 0 ||
                            isFiltersSelected))) && (
                        <IFFilter
                          key="IFFilter_Logs"
                          onFilterChange={(newFilter) => {
                            handleChargingSessionLogs(newFilter)
                            setIsFiltersSelected(true)
                          }}
                          filters={chargingSessionLogsFilter}
                          textFieldPlaceholder={t(
                            'ChargePointDetails.FilterLogsBy',
                          )}
                          isLoading={
                            fetchChargingSessionLogsRequestState ===
                            RequestState.LOADING
                          }
                        />
                      )}
                    </div>
                    <ChargingSessionLogsList
                      chargingSessionId={chargingSessionId}
                      fetchChargingSessionLogsRequestState={
                        fetchChargingSessionLogsRequestState
                      }
                      chargingSessionLogs={chargingSessionLogs}
                    />
                  </div>
                )}
              </div>
            </div>
          </Scrollbars>
        </div>
      </IFModal>
    )
  },
)

const mapStateToProps = (state) => ({
  chargingSessionDetails: ChargePointSelectors.getChargingSessionDetails(state),
  fetchChargingSessionDetailsRequestState:
    ChargePointSelectors.getFetchChargingSessionDetailsRequestState(state),
  chargingSessionEvents: ChargePointSelectors.getChargingSessionEvents(state),
  chargingSessionLogs: ChargePointSelectors.getChargingSessionLogs(state),
  fetchChargingSessionLogsRequestState:
    ChargePointSelectors.getFetchChargingSessionLogsRequestState(state),
  fetchChargingSessionEventsRequestState:
    ChargePointSelectors.getFetchChargingSessionEventsRequestState(state),
  chargingLogsPaginationOffset:
    ChargePointSelectors.getChargingSessionLogsPaginationOffset(state),
  chargingSessionLogsFilter:
    ChargePointSelectors.getChargingSessionLogsFilter(state),
  chargePointConnectors: ChargePointSelectors.getChargePointConnectors(state),
  operator: AuthSelectors.getOperator(state),
  chargePoints: ChargePointSelectors.getChargePoints(state),
  isChargingAnalyticsLive:
    ChargePointSelectors.getIsChargingAnalyticsLiveLive(state),
})

function mapDispatchToProps(dispatch) {
  return {
    fetchChargingSessionDetails: (chargingSessionId) =>
      dispatch(
        ChargePointActions.fetchChargingSessionDetails(chargingSessionId),
      ),
    fetchChargingSessionEvents: (chargingSessionId) =>
      dispatch(
        ChargePointActions.fetchChargingSessionEvents(chargingSessionId),
      ),
    fetchChargingSessionLogs: (chargingSessionId, offset, filter) =>
      dispatch(
        ChargePointActions.fetchChargingSessionLogs(
          chargingSessionId,
          offset,
          filter,
        ),
      ),
    resetChargingSessionEvents: () =>
      dispatch(ChargePointActions.resetChargingSessionEvents()),
    resetChargingSessionLogs: () =>
      dispatch(ChargePointActions.resetChargingSessionLogs()),
    setChargingSessionLogPaginationOffset: (offset) =>
      dispatch(
        ChargePointActions.setChargingSessionLogPaginationOffset(offset),
      ),
    setChargingSessionLogsFilter: (newFilter) =>
      dispatch(ChargePointActions.setChargingSessionLogsFilter(newFilter)),
    resetChargingSessionLogsFilters: () =>
      dispatch(ChargePointActions.resetChargingSessionLogsFilters()),
    setChargingSessionLogsPaginationOffset: (offset) =>
      dispatch(
        ChargePointActions.setChargingSessionLogsPaginationOffset(offset),
      ),
    emitterUpdateChargingEvents: (data) =>
      dispatch(ChargePointActions.emitterUpdateChargingEvents(data)),
    emitterUpdateChargingSessionDetails: (data) =>
      dispatch(ChargePointActions.emitterUpdateChargingSessionDetails(data)),
    setIsChargingAnalyticsLive: (flag) =>
      dispatch(ChargePointActions.setIsChargingAnalyticsLive(flag)),
    resetChargingSessionDetails: () =>
      dispatch(ChargePointActions.resetChargingSessionDetails()),
  }
}

export default connect(mapStateToProps, mapDispatchToProps, null, {
  forwardRef: true,
})(ChargingEventsModal)
